import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { NO_ERRORS_SCHEMA, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { GoogleMapsModule } from '@angular/google-maps';
import { NgbDateParserFormatter, NgbDatepickerModule, NgbModule, NgbTimepickerModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule } from '@ngx-translate/core';
import { NgxIntlTelInputModule } from 'ngx-intl-tel-input';
import { SecurityAdminDetailComponent } from 'src/app/macro-community/pages/door-opening-macro-community/security-admin-detail/security-admin-detail.component';
import { AccessTimelineComponent } from 'src/app/pages/door-opening/access-timeline/access-timeline.component';
import { UserAdministrationService } from '../macro-community/services/administration/user.service';
import { BuildingMacroCommunityService } from '../macro-community/services/building.service';
import { SafeHtmlPipe } from '../pipes/safeHtmlPipe.pipe';
import { MunilyPipesModule } from '../pipes/munily-pipes.module';
import { ApartmentService } from '../services/apartment.service';
import { PlanService } from '../services/plan.service';
import { NgbDateCustomParserFormatter } from '../shared/ngb-datepicker-formatter/ngbDateCustomParserFormatter';
import { AdvanceSearchComponent } from './advance-search/advance-search.component';
import { ButtonsFilledComponent } from './buttons/buttons-filled/buttons-filled.component';
import { ButtonsOutlinedComponent } from './buttons/buttons-outlined/buttons-outlined.component';
import { TextButtonComponent } from './buttons/text-button/text-button.component';
import { ChangePasswordComponent } from './change-password/change-password.component';
import { CommonFiltersComponent } from './common-filters/common-filters.component';
import { DatepickerComponent } from './datepicker/datepicker.component';
import { CustomDatepickerHeader } from './datepickers/custom-datepickers/custom-datepicker-header';
import { CustomDatepickersComponent } from './datepickers/custom-datepickers/custom-datepickers.component';
import { DynamicTableComponent } from './dynamic-table/dynamic-table.component';
import { EmptyDatatableComponent } from './empty-datatable/empty-datatable.component';
import { FiltersComponent } from './filters/filters.component';
import { InputOutlineComponent } from './input/input-outline/input-outline.component';
import { LoadingComponent } from './loading.component';
import { MatMenuTemplateComponent } from './mat-menu/mat-menu-template/mat-menu-template.component';
import { MetricChartComponent } from './metric-chart/metric-chart.component';
import { ModalConfirmationComponent } from './modal-confirmation/modal-confirmation.component';
import { ModalMapBuildingsComponent } from './modal-map-buildings/modal-map-buildings.component';
import { ModalComponent } from './modal/modal.component';
import { ConfirmationModalComponent } from './modals/confirmation-modal/confirmation-modal.component';
import { ErrorModalComponent } from './modals/error-modal/error-modal.component';
import { SuccessModalComponent } from './modals/success-modal/success-modal.component';
import { SingleFieldFormModalComponent } from './modals/single-field-form-modal/single-field-form-modal.component';
import { SingleTimeFieldFormModalComponent } from './modals/single-time-field-form-modal/single-time-field-form-modal.component';
import { SingleSelectFieldFormModalComponent } from './modals/single-select-field-form-modal/single-select-field-form-modal.component';
import { MultipleSelectFieldFormModalComponent } from './modals/multiple-select-field-form-modal/multiple-select-field-form-modal.component';
import { MultiSelectFieldsFormModalComponent } from './modals/multi-select-fields-form-modal/multi-select-fields-form-modal.component';
import { SinglePhoneFieldFormModalComponent } from './modals/single-phone-field-form-modal/single-phone-field-form-modal.component';
import { SingleTextAreaFieldFormModalComponent } from './modals/single-text-area-field-form-modal/single-text-area-field-form-modal.component';
import { AreaIconPickerModalComponent } from './modals/area-icon-picker-modal/area-icon-picker-modal.component';
import { ChangePasswordFormModalComponent } from './modals/change-password-form-modal/change-password-form-modal.component';
import { SingleSelectInputComponent } from './input/single-select-input/single-select-input.component';
import { ProgressBarCountComponent } from './progress-bar/progress-bar-count/progress-bar-count.component';
import { SelectOutlineComponent } from './select/select-outline/select-outline.component';
import { TableMasterComponent } from './table-master/table-master.component';
import { ToggleComponent } from './toggle/toggle.component';
import { SecondaryButtonsFilledComponent } from './buttons/secondary-button-filled/secondary-button-filled.component';
import { IconButtonComponent } from './buttons/icon-button/icon-button.component';

import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import { MatPaginatorModule } from '@angular/material/paginator';
import { CheckboxComponent } from './checkbox/checkbox.component';
import { CheckboxListComponent } from './checkbox-list/checkbox-list.component';
import { FileUploaderAppComponent } from './file-uploader/file-uploader-app/file-uploader-app.component';
import { FileDropUploaderComponent } from './file-drop-uploader/file-drop-uploader.component';
import { PreviewImageComponent } from './modals/preview-image/preview-image.component';
import { InfoChipComponent } from './info-chip/info-chip.component';
import { FilterChipComponent } from './filter-chip/filter-chip.component';
import { TextFieldComponent } from './text-field/text-field.component';
import { DateFieldComponent } from './date-field/date-field.component';
import { MunilyDatePickerModalComponent } from './date-picker-modal/date-picker-modal.component';
import { TimeInputFieldComponent } from './time-field/time-input-field/time-input-field.component';
import { SelectFieldComponent } from './select-field/select-field.component';
import { FilterTextFieldComponent } from './filter-text-field/filter-text-field.component';
import { FilterSelectFieldComponent } from './filter-select-field/filter-select-field.component';
import { BadgeComponent } from './badge/badge.component';
import { TabsComponent } from './tabs/tabs.component';
import { MetricCardsComponent } from './metric-cards/metric-cards.component';
import { SelectableOptionCardComponent } from './selectable-option-card/selectable-option-card.component';
import { TooltipComponent } from './tooltip/tooltip.component';
import { CardComponent } from './card/card.component';
import { FrequentAccessCardComponent } from './cards/frequent-access-card/frequent-access-card.component';
import { ScheduleCardComponent } from './cards/schedule-card/schedule-card.component';
import { SegmentedControlComponent } from './segmented-control/segmented-control.component';
import { IconAreaCardComponent } from './icon-area-card/icon-area-card.component';
import { AreaCardComponent } from './area-card/area-card.component';
import { PaginationButtonsComponent } from './pagination-buttons/pagination-buttons.component';
import { TextAreaFieldComponent } from './text-area-field/text-area-field.component';
import { QuillModule } from 'ngx-quill';
import { AvatarComponent } from './avatar/avatar.component';
import { AreaPictureUploaderComponent } from './area-picture-uploader/area-picture-uploader.component';
import { TitlePreviewComponent } from './title-preview/title-preview.component';
import { DescriptionPreviewComponent } from './description-preview/description-preview.component';
import { InputPreviewComponent } from './input-preview/input-preview.component';
import { ReservationReviewCardComponent } from './reservation-review-card/reservation-review-card.component';
import { AreaPictureUploaderGalleryComponent } from './area-picture-uploader-gallery/area-picture-uploader-gallery.component';
import { ImagePreviewIconActionComponent } from './image-preview-icon-action/image-preview-icon-action.component';
import { DropdownMenuComponent } from './dropdown-menu/dropdown-menu.component';
import { PanelComponent } from './panel/panel.component';
import { TogglePanelComponent } from './toggle-panel/toggle-panel.component';
import { WizardStepperComponent } from './wizard-stepper/wizard-stepper.component';
import { AlertComponent } from './alert/alert.component';
import { NotificationCardComponent } from './notification-card/notification-card.component';
import { NestedResourceCardComponent } from './nested-resource-card/nested-resource-card.component';
import { QtyStepperComponent } from './qty-stepper/qty-stepper.component';
import { FroalaEditorModule } from 'angular-froala-wysiwyg';

@NgModule({
  schemas: [NO_ERRORS_SCHEMA], //TODO: Remover cuando se migren las librerias
  imports: [
    CommonModule,
    DragDropModule,
    FormsModule,
    ReactiveFormsModule,
    NgxIntlTelInputModule,
    GoogleMapsModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatNativeDateModule,
    TranslateModule.forChild(),
    NgSelectModule,
    NgbDatepickerModule,
    NgbTimepickerModule,
    NgbTooltipModule,
    NgbModule,
    MatButtonModule,
    MatIconModule,
    MatAutocompleteModule,
    MatSelectModule,
    MatInputModule,
    MatMenuModule,
    MatDialogModule,
    MatSlideToggleModule,
    MatCheckboxModule,
    MatRadioModule,
    MatTabsModule,
    MatTooltipModule,
    MatCardModule,
    MatPaginatorModule,
    FroalaEditorModule,
    MunilyPipesModule,
    QuillModule,
  ],
  declarations: [
    ModalConfirmationComponent,
    TableMasterComponent,
    LoadingComponent,
    AdvanceSearchComponent,
    SafeHtmlPipe,
    ChangePasswordComponent,
    MetricChartComponent,
    EmptyDatatableComponent,
    ModalMapBuildingsComponent,
    ModalComponent,
    ConfirmationModalComponent,
    ErrorModalComponent,
    SuccessModalComponent,
    SingleFieldFormModalComponent,
    SingleTimeFieldFormModalComponent,
    SingleSelectFieldFormModalComponent,
    SingleTextAreaFieldFormModalComponent,
    AreaIconPickerModalComponent,
    MultipleSelectFieldFormModalComponent,
    MultiSelectFieldsFormModalComponent,
    SinglePhoneFieldFormModalComponent,
    ChangePasswordFormModalComponent,
    SingleSelectInputComponent,
    ButtonsFilledComponent,
    SecondaryButtonsFilledComponent,
    ButtonsOutlinedComponent,
    TextButtonComponent,
    IconButtonComponent,
    InputOutlineComponent,
    SelectOutlineComponent,
    DatepickerComponent,
    CustomDatepickerHeader,
    CustomDatepickersComponent,
    DynamicTableComponent,
    FiltersComponent,
    CommonFiltersComponent,
    AdvanceSearchComponent,
    ProgressBarCountComponent,
    ToggleComponent,
    MatMenuTemplateComponent,
    SecurityAdminDetailComponent,
    AccessTimelineComponent,
    CheckboxComponent,
    CheckboxListComponent,
    FileUploaderAppComponent,
    FileDropUploaderComponent,
    PreviewImageComponent,
    InfoChipComponent,
    FilterChipComponent,
    TextFieldComponent,
    DateFieldComponent,
    MunilyDatePickerModalComponent,
    TimeInputFieldComponent,
    SelectFieldComponent,
    FilterTextFieldComponent,
    FilterSelectFieldComponent,
    FilterChipComponent,
    BadgeComponent,
    TabsComponent,
    MetricCardsComponent,
    SelectableOptionCardComponent,
    TooltipComponent,
    CardComponent,
    FrequentAccessCardComponent,
    ScheduleCardComponent,
    SegmentedControlComponent,
    IconAreaCardComponent,
    AreaCardComponent,
    PaginationButtonsComponent,
    TextAreaFieldComponent,
    AvatarComponent,
    AreaPictureUploaderComponent,
    TitlePreviewComponent,
    DescriptionPreviewComponent,
    InputPreviewComponent,
    ReservationReviewCardComponent,
    AreaPictureUploaderGalleryComponent,
    ImagePreviewIconActionComponent,
    DropdownMenuComponent,
    PanelComponent,
    TogglePanelComponent,
    WizardStepperComponent,
    AlertComponent,
    NotificationCardComponent,
    NestedResourceCardComponent,
    WizardStepperComponent,
    QtyStepperComponent,
    SegmentedControlComponent,
  ],
  exports: [
    ModalConfirmationComponent,
    TableMasterComponent,
    LoadingComponent,
    AdvanceSearchComponent,
    SafeHtmlPipe,
    ChangePasswordComponent,
    MetricChartComponent,
    EmptyDatatableComponent,
    ModalMapBuildingsComponent,
    ModalComponent,
    ConfirmationModalComponent,
    ErrorModalComponent,
    SuccessModalComponent,
    SingleFieldFormModalComponent,
    SingleTimeFieldFormModalComponent,
    SingleSelectFieldFormModalComponent,
    SingleTextAreaFieldFormModalComponent,
    AreaIconPickerModalComponent,
    MultipleSelectFieldFormModalComponent,
    MultiSelectFieldsFormModalComponent,
    SinglePhoneFieldFormModalComponent,
    ChangePasswordFormModalComponent,
    SingleSelectInputComponent,
    ButtonsFilledComponent,
    SecondaryButtonsFilledComponent,
    ButtonsOutlinedComponent,
    TextButtonComponent,
    IconButtonComponent,
    InputOutlineComponent,
    SelectOutlineComponent,
    DatepickerComponent,
    CustomDatepickerHeader,
    CustomDatepickersComponent,
    DynamicTableComponent,
    FiltersComponent,
    CommonFiltersComponent,
    AdvanceSearchComponent,
    ProgressBarCountComponent,
    ToggleComponent,
    MatMenuTemplateComponent,
    SecurityAdminDetailComponent,
    AccessTimelineComponent,
    CheckboxComponent,
    CheckboxListComponent,
    GoogleMapsModule,
    FileUploaderAppComponent,
    FileDropUploaderComponent,
    InfoChipComponent,
    FilterChipComponent,
    TextFieldComponent,
    DateFieldComponent,
    MunilyDatePickerModalComponent,
    TimeInputFieldComponent,
    SelectFieldComponent,
    FilterTextFieldComponent,
    FilterSelectFieldComponent,
    FilterChipComponent,
    BadgeComponent,
    TabsComponent,
    MetricCardsComponent,
    SelectableOptionCardComponent,
    TooltipComponent,
    CardComponent,
    FrequentAccessCardComponent,
    ScheduleCardComponent,
    SegmentedControlComponent,
    IconAreaCardComponent,
    AreaCardComponent,
    PaginationButtonsComponent,
    TextAreaFieldComponent,
    AvatarComponent,
    AreaPictureUploaderComponent,
    TitlePreviewComponent,
    DescriptionPreviewComponent,
    InputPreviewComponent,
    ReservationReviewCardComponent,
    AreaPictureUploaderGalleryComponent,
    ImagePreviewIconActionComponent,
    DropdownMenuComponent,
    PanelComponent,
    TogglePanelComponent,
    WizardStepperComponent,
    AlertComponent,
    NotificationCardComponent,
    NestedResourceCardComponent,
    MunilyPipesModule,
    QtyStepperComponent,
    SegmentedControlComponent,
    // Angular Material modules
    MatButtonModule,
    MatIconModule,
    MatAutocompleteModule,
    MatSelectModule,
    MatInputModule,
    MatMenuModule,
    MatDialogModule,
    MatSlideToggleModule,
    MatCheckboxModule,
    MatRadioModule,
    MatTabsModule,
    MatTooltipModule,
    MatCardModule,
    MatPaginatorModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatNativeDateModule,
    // Translate module
    TranslateModule,
    // Phone input module
    NgxIntlTelInputModule,
  ],
  providers: [
    {
      provide: NgbDateParserFormatter,
      useClass: NgbDateCustomParserFormatter,
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: 'es-ES',
    },
    UserAdministrationService,
    BuildingMacroCommunityService,
    ApartmentService,
    PlanService,
  ]
})
export class CommonComponentsModule { }
